import type { AttachmentFileSystem } from "@note-app/knowledge-services";
import type { IDBPDatabase } from "idb";

import {
  IndexedDbStorageError,
  mapIndexedDbError,
} from "./errors.ts";
import type { KnowledgeDbSchema } from "./schema.ts";

export interface StorageEstimate {
  quota?: number;
  usage?: number;
}

export interface BrowserBlobStoreOptions {
  readonly database: IDBPDatabase<KnowledgeDbSchema>;
  readonly estimate?: () => Promise<StorageEstimate>;
  readonly getOpfsRoot?: () => Promise<FileSystemDirectoryHandle>;
  readonly reserveBytes?: number;
  readonly now?: () => number;
  /** Maximum wait for one browser-storage operation before falling back/failing. */
  readonly operationTimeoutMs?: number;
}

/**
 * Attachment bytes prefer OPFS. If OPFS is unavailable, IndexedDB is used
 * only after an explicit quota headroom check.
 */
export class BrowserAttachmentBlobStore implements AttachmentFileSystem {
  private readonly temps = new Map<string, Uint8Array[]>();
  private readonly estimate: () => Promise<StorageEstimate>;
  private readonly getOpfsRoot:
    | (() => Promise<FileSystemDirectoryHandle>)
    | undefined;
  private readonly reserveBytes: number;
  private readonly now: () => number;
  private readonly operationTimeoutMs: number;
  private backend: "opfs" | "indexeddb" | undefined;
  private opfsWasAvailable = false;

  public constructor(private readonly options: BrowserBlobStoreOptions) {
    this.estimate =
      options.estimate ??
      (async () => (await navigator.storage.estimate()) as StorageEstimate);
    this.getOpfsRoot =
      options.getOpfsRoot ??
      (typeof navigator !== "undefined" &&
      "storage" in navigator &&
      "getDirectory" in navigator.storage
        ? () => navigator.storage.getDirectory()
        : undefined);
    this.reserveBytes = options.reserveBytes ?? 5 * 1024 * 1024;
    this.now = options.now ?? Date.now;
    this.operationTimeoutMs = options.operationTimeoutMs ?? 12_000;
  }

  public async storageBackend(): Promise<"opfs" | "indexeddb"> {
    if (this.backend) return this.backend;
    if (this.getOpfsRoot) {
      try {
        await withStorageTimeout(
          this.getOpfsRoot(),
          this.operationTimeoutMs,
          "Opening OPFS took too long.",
        );
        this.opfsWasAvailable = true;
        this.backend = "opfs";
        return this.backend;
      } catch {
        // OPFS is optional; the fallback remains quota guarded.
      }
    }
    this.backend = "indexeddb";
    return this.backend;
  }

  async createTemp(): Promise<string> {
    const id = `temp-${crypto.randomUUID()}`;
    this.temps.set(id, []);
    return id;
  }

  async appendTemp(tempId: string, chunk: Uint8Array): Promise<void> {
    const chunks = this.temps.get(tempId);
    if (!chunks) throw new Error(`Unknown temporary attachment ${tempId}`);
    chunks.push(chunk.slice());
  }

  async commitTemp(
    tempId: string,
    storageKey: string,
  ): Promise<"created" | "already-exists"> {
    const chunks = this.temps.get(tempId);
    if (!chunks) throw new Error(`Unknown temporary attachment ${tempId}`);
    const bytes = join(chunks);
    try {
      if ((await this.storageBackend()) === "opfs") {
        try {
          const result = await this.commitOpfs(storageKey, bytes);
          this.temps.delete(tempId);
          return result;
        } catch (opfsError) {
          // OPFS support can disappear or stall during a session. Prefer a
          // working IndexedDB write over leaving the editor in a permanent
          // loading state, while retaining hybrid reads for older OPFS data.
          this.backend = "indexeddb";
          try {
            const result = await this.commitIndexedDb(storageKey, bytes);
            this.temps.delete(tempId);
            return result;
          } catch (indexedDbError) {
            if (indexedDbError instanceof IndexedDbStorageError) {
              throw indexedDbError;
            }
            throw new AggregateError(
              [opfsError, indexedDbError],
              "Neither OPFS nor IndexedDB could store the attachment.",
            );
          }
        }
      }
      const result = await this.commitIndexedDb(storageKey, bytes);
      this.temps.delete(tempId);
      return result;
    } catch (error) {
      throw mapIndexedDbError(
        error,
        "Attachment bytes could not be stored.",
      );
    }
  }

  async discardTemp(tempId: string): Promise<void> {
    this.temps.delete(tempId);
  }

  async *read(storageKey: string): AsyncIterable<Uint8Array> {
    const backend = await this.storageBackend();
    if (backend === "indexeddb") {
      const indexedDbBytes = await this.readIndexedDb(storageKey);
      if (indexedDbBytes) {
        yield indexedDbBytes;
        return;
      }
      if (!this.opfsWasAvailable) {
        throw new Error(`Attachment ${storageKey} is missing`);
      }
      const opfsBytes = await this.readOpfsOrNull(storageKey);
      if (opfsBytes) {
        yield opfsBytes;
        return;
      }
    } else {
      try {
        const opfsBytes = await this.readOpfsOrNull(storageKey);
        if (opfsBytes) {
          yield opfsBytes;
          return;
        }
      } catch (opfsError) {
        this.backend = "indexeddb";
        const indexedDbBytes = await this.readIndexedDb(storageKey);
        if (indexedDbBytes) {
          yield indexedDbBytes;
          return;
        }
        throw opfsError;
      }
      const indexedDbBytes = await this.readIndexedDb(storageKey);
      if (indexedDbBytes) {
        yield indexedDbBytes;
        return;
      }
    }
    throw new Error(`Attachment ${storageKey} is missing`);
  }

  async stat(
    storageKey: string,
  ): Promise<{ byteLength: number; modifiedAt?: number } | null> {
    const backend = await this.storageBackend();
    if (backend === "indexeddb") {
      const indexedDbStat = await this.statIndexedDb(storageKey);
      if (indexedDbStat) return indexedDbStat;
      if (!this.opfsWasAvailable) return null;
      try {
        return await this.statOpfsOrNull(storageKey);
      } catch {
        return null;
      }
    }
    try {
      const opfsStat = await this.statOpfsOrNull(storageKey);
      if (opfsStat) return opfsStat;
    } catch {
      this.backend = "indexeddb";
      const indexedDbStat = await this.statIndexedDb(storageKey);
      if (indexedDbStat) return indexedDbStat;
      // `stat` is also used before repairing a deduplicated attachment. The
      // caller already owns verified bytes, so reporting a miss allows those
      // bytes to be restored into the healthy fallback backend.
      return null;
    }
    return this.statIndexedDb(storageKey);
  }

  async delete(storageKey: string): Promise<void> {
    const opfsExpected = (await this.storageBackend()) === "opfs";
    let opfsError: unknown;
    if (this.getOpfsRoot) {
      try {
        const root = await this.requireOpfsRoot();
        await withStorageTimeout(
          root.removeEntry(storageKey),
          this.operationTimeoutMs,
          "Deleting the OPFS attachment took too long.",
        );
      } catch (error) {
        if (!isNotFound(error)) opfsError = error;
      }
    }
    await withStorageTimeout(
      this.options.database.delete("blobs", storageKey),
      this.operationTimeoutMs,
      "Deleting the IndexedDB attachment took too long.",
    );
    if (opfsError && opfsExpected) throw opfsError;
  }

  async *listBlobs(): AsyncIterable<{
    storageKey: string;
    byteLength: number;
    modifiedAt: number;
  }> {
    await this.storageBackend();
    const entries = new Map<
      string,
      { storageKey: string; byteLength: number; modifiedAt: number }
    >();
    const rows = await withStorageTimeout(
      this.options.database.getAll("blobs"),
      this.operationTimeoutMs,
      "Listing IndexedDB attachments took too long.",
    );
    for (const row of rows) {
      entries.set(row.storageKey, {
        storageKey: row.storageKey,
        byteLength: row.byteLength,
        modifiedAt: row.modifiedAt,
      });
    }
    if (this.getOpfsRoot) {
      try {
        const root = await this.requireOpfsRoot();
        const iterableRoot = root as FileSystemDirectoryHandle & {
          entries(): AsyncIterableIterator<[string, FileSystemHandle]>;
        };
        const iterator = iterableRoot.entries();
        while (true) {
          const result = await withStorageTimeout(
            iterator.next(),
            this.operationTimeoutMs,
            "Listing OPFS attachments took too long.",
          );
          if (result.done) break;
          const [storageKey, handle] = result.value;
          if (handle.kind !== "file") continue;
          const file = await withStorageTimeout(
            (handle as FileSystemFileHandle).getFile(),
            this.operationTimeoutMs,
            "Reading OPFS attachment metadata took too long.",
          );
          entries.set(storageKey, {
            storageKey,
            byteLength: file.size,
            modifiedAt: file.lastModified,
          });
        }
      } catch (error) {
        if (this.opfsWasAvailable) throw error;
      }
    }
    for (const entry of entries.values()) {
      yield entry;
    }
  }

  private async assertQuota(incomingBytes: number): Promise<void> {
    const { quota, usage } = await withStorageTimeout(
      this.estimate(),
      this.operationTimeoutMs,
      "Checking browser storage capacity took too long.",
    );
    if (
      quota !== undefined &&
      usage !== undefined &&
      quota - usage - this.reserveBytes < incomingBytes
    ) {
      throw new IndexedDbStorageError(
        "quota-exceeded",
        "Not enough browser storage remains for this attachment. Export a backup or free device space.",
      );
    }
  }

  private async requireOpfsRoot(): Promise<FileSystemDirectoryHandle> {
    if (!this.getOpfsRoot) {
      throw new IndexedDbStorageError(
        "unavailable",
        "OPFS is unavailable in this browser.",
      );
    }
    return withStorageTimeout(
      this.getOpfsRoot(),
      this.operationTimeoutMs,
      "Opening OPFS took too long.",
    );
  }

  private async commitOpfs(
    storageKey: string,
    bytes: Uint8Array,
  ): Promise<"created" | "already-exists"> {
    const root = await this.requireOpfsRoot();
    const existing = await getOpfsFile(
      root,
      storageKey,
      this.operationTimeoutMs,
    );
    if (existing) return "already-exists";
    await this.assertQuota(bytes.byteLength);
    const handle = await withStorageTimeout(
      root.getFileHandle(storageKey, { create: true }),
      this.operationTimeoutMs,
      "Creating the OPFS attachment took too long.",
    );
    const writable = await withStorageTimeout(
      handle.createWritable({ keepExistingData: false }),
      this.operationTimeoutMs,
      "Opening the OPFS attachment for writing took too long.",
    );
    try {
      await withStorageTimeout(
        writable.write(new Uint8Array(bytes).buffer),
        this.operationTimeoutMs,
        "Writing the OPFS attachment took too long.",
      );
      await withStorageTimeout(
        writable.close(),
        this.operationTimeoutMs,
        "Finishing the OPFS attachment took too long.",
      );
      return "created";
    } catch (error) {
      void writable.abort().catch(() => undefined);
      void root.removeEntry(storageKey).catch(() => undefined);
      throw error;
    }
  }

  private async commitIndexedDb(
    storageKey: string,
    bytes: Uint8Array,
  ): Promise<"created" | "already-exists"> {
    const existing = await withStorageTimeout(
      this.options.database.get("blobs", storageKey),
      this.operationTimeoutMs,
      "Checking IndexedDB attachments took too long.",
    );
    if (existing) return "already-exists";
    await this.assertQuota(bytes.byteLength);
    try {
      await withStorageTimeout(
        this.options.database.add("blobs", {
          storageKey,
          bytes,
          byteLength: bytes.byteLength,
          modifiedAt: this.now(),
        }),
        this.operationTimeoutMs,
        "Writing the IndexedDB attachment took too long.",
      );
    } catch (error) {
      if (isConstraintViolation(error)) return "already-exists";
      throw error;
    }
    return "created";
  }

  private async readIndexedDb(storageKey: string): Promise<Uint8Array | null> {
    const row = await withStorageTimeout(
      this.options.database.get("blobs", storageKey),
      this.operationTimeoutMs,
      "Reading the IndexedDB attachment took too long.",
    );
    return row?.bytes.slice() ?? null;
  }

  private async readOpfsOrNull(storageKey: string): Promise<Uint8Array | null> {
    if (!this.getOpfsRoot) return null;
    const root = await this.requireOpfsRoot();
    const handle = await getOpfsFile(
      root,
      storageKey,
      this.operationTimeoutMs,
    );
    if (!handle) return null;
    const file = await withStorageTimeout(
      handle.getFile(),
      this.operationTimeoutMs,
      "Opening the OPFS attachment took too long.",
    );
    const buffer = await withStorageTimeout(
      file.arrayBuffer(),
      this.operationTimeoutMs,
      "Reading the OPFS attachment took too long.",
    );
    return new Uint8Array(buffer);
  }

  private async statIndexedDb(
    storageKey: string,
  ): Promise<{ byteLength: number; modifiedAt?: number } | null> {
    const row = await withStorageTimeout(
      this.options.database.get("blobs", storageKey),
      this.operationTimeoutMs,
      "Reading IndexedDB attachment metadata took too long.",
    );
    return row
      ? { byteLength: row.byteLength, modifiedAt: row.modifiedAt }
      : null;
  }

  private async statOpfsOrNull(
    storageKey: string,
  ): Promise<{ byteLength: number; modifiedAt?: number } | null> {
    if (!this.getOpfsRoot) return null;
    const root = await this.requireOpfsRoot();
    const handle = await getOpfsFile(
      root,
      storageKey,
      this.operationTimeoutMs,
    );
    if (!handle) return null;
    const file = await withStorageTimeout(
      handle.getFile(),
      this.operationTimeoutMs,
      "Reading OPFS attachment metadata took too long.",
    );
    return { byteLength: file.size, modifiedAt: file.lastModified };
  }
}

async function getOpfsFile(
  root: FileSystemDirectoryHandle,
  storageKey: string,
  timeoutMs: number,
): Promise<FileSystemFileHandle | null> {
  try {
    return await withStorageTimeout(
      root.getFileHandle(storageKey),
      timeoutMs,
      "Finding the OPFS attachment took too long.",
    );
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
}

function isNotFound(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "NotFoundError"
  );
}

function isConstraintViolation(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "ConstraintError"
  );
}

class BrowserStorageTimeoutError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = "BrowserStorageTimeoutError";
  }
}

function withStorageTimeout<T>(
  operation: PromiseLike<T>,
  timeoutMs: number,
  message: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timer);
      callback();
    };
    const timer = globalThis.setTimeout(
      () => finish(() => reject(new BrowserStorageTimeoutError(message))),
      Math.max(1, timeoutMs),
    );
    Promise.resolve(operation).then(
      (value) => finish(() => resolve(value)),
      (error: unknown) => finish(() => reject(error)),
    );
  });
}

function join(chunks: readonly Uint8Array[]): Uint8Array {
  const result = new Uint8Array(
    chunks.reduce((total, chunk) => total + chunk.byteLength, 0),
  );
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return result;
}
